import { ChangeEvent, FocusEvent, useState } from "react";
import {
  FormikSetFieldTouched,
  FormikSetFieldValue,
} from "@/shared/types/formik";
import {
  NumericInputAttributes,
  numericInputProps,
  numericInputValue,
  readNumericInput,
  showNumericError,
} from "@/shared/utils/numeric-input";

interface NumericFieldConfig {
  // Formik field name; doubles as the key focus is tracked under.
  name: string;
  value: number | undefined;
  error: string | undefined;
  touched: boolean | undefined;
  allowDecimal?: boolean;
  allowStepping?: boolean;
  // Applied to parsed input before it is stored (Difficulty clamps to 1–5).
  clamp?: (value: number) => number;
  // See showNumericError: false for a field whose initial value is a placeholder
  // that fails its own rule (Service Duration starts at 0 and must be >= 1).
  reportUntouched?: boolean;
}

export interface NumericFieldApi {
  // Safe for a controlled input: undefined and NaN both render as "".
  value: number | "";
  // Whether this field's message belongs on screen right now.
  showError: boolean;
  // The message, or undefined while it should stay hidden.
  helperText: string | undefined;
  onChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus: () => void;
  onBlur: (event: FocusEvent<HTMLInputElement>) => void;
  // For controls that aren't the text input itself (the Difficulty slider).
  setValue: (value: number | undefined) => void;
  // Native input attributes: inputMode plus the key/paste/wheel guards.
  inputProps: NumericInputAttributes;
}

// The behaviour shared by the CRM `type="number"` fields: marshalling input into
// Formik (including the NaN marker for unparseable content), tracking which field
// holds the caret so a half-typed decimal doesn't flash an error, and deciding
// when a message is due. Presentation — adornments, width, where the message
// renders — stays with each field. A single focus slot is enough, since only one
// field can be focused at a time.
export const useNumericFields = ({
  setFieldValue,
  setFieldTouched,
  handleBlur,
}: {
  setFieldValue: FormikSetFieldValue;
  setFieldTouched: FormikSetFieldTouched;
  handleBlur: (event: FocusEvent<HTMLInputElement>) => void;
}) => {
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const field = ({
    name,
    value,
    error,
    touched,
    allowDecimal,
    allowStepping,
    clamp,
    reportUntouched,
  }: NumericFieldConfig): NumericFieldApi => {
    const showError = showNumericError({
      touched,
      value,
      error,
      focused: focusedField === name,
      reportUntouched,
    });

    const setValue = (next: number | undefined) => {
      setFieldValue(name, next);
      // setFieldValue already validated with the new value; setFieldTouched must
      // NOT re-validate — it runs against the stale value and its result lands
      // last, wiping the error just computed (the reason these fields showed no
      // message at all before ESR-1566).
      setFieldTouched(name, true, false);
    };

    return {
      value: numericInputValue(value),
      showError,
      helperText: showError ? error : undefined,
      onChange: (event) => {
        const parsed = readNumericInput(event);
        // Clearing resets to undefined and unparseable input keeps its NaN, so
        // the schema reports it; only a real number is clamped.
        setValue(
          clamp && parsed !== undefined && !Number.isNaN(parsed)
            ? clamp(parsed)
            : parsed,
        );
      },
      onFocus: () => setFocusedField(name),
      onBlur: (event) => {
        setFocusedField(null);
        handleBlur(event);
      },
      setValue,
      inputProps: numericInputProps({ allowDecimal, allowStepping }),
    };
  };

  return { field };
};
